import { useQuery } from "@tanstack/react-query";

export interface PepWindowStatus {
  window: { id: string; name: string; windowType: string; startDate: string; endDate: string; applicableGrades: number[] };
  daysUntil: number;
  isActive: boolean;
}

/** Live PEP windows for a grade from the academic calendar (public endpoint, cached for an hour). */
export function usePepWindows(grade: number, enabled = true) {
  return useQuery<PepWindowStatus[]>({
    queryKey: ["/api/academic-calendar/pep-status", grade],
    queryFn: async () => {
      const r = await fetch(`/api/academic-calendar/pep-status?grade=${grade}`, { credentials: "include" });
      if (!r.ok) throw new Error("pep-status");
      const d = await r.json();
      return (d?.pepWindows ?? []) as PepWindowStatus[];
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  });
}

const SUBJECT_MATCHERS: Array<{ test: RegExp; window: RegExp }> = [
  { test: /math/i, window: /math/i },
  { test: /english|language/i, window: /language arts|english/i },
  { test: /science/i, window: /science/i },
  { test: /social/i, window: /social/i },
  { test: /ability/i, window: /ability/i },
];

export interface SubjectCountdown { days: number; date: Date; isActive: boolean; windowName: string }

const JAMAICA = "America/Jamaica";

/** Calendar day (YYYY-MM-DD) of an instant, as seen in Jamaica. Exams happen on Jamaica days, wherever the browser is. */
function jamaicaDay(d: Date): string {
  return d.toLocaleDateString("en-CA", { timeZone: JAMAICA, year: "numeric", month: "2-digit", day: "2-digit" });
}
function dayNumber(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

/** Whole days from today (Jamaica) to the sitting; 0 on the day, negative once it has passed. */
export function daysUntil(instant: string | Date, now: Date = new Date()): number {
  return dayNumber(jamaicaDay(new Date(instant))) - dayNumber(jamaicaDay(now));
}

/** Countdown for one window, computed client-side in Jamaica time (the server's count is a fallback only). */
export function countdownForWindow(w: PepWindowStatus, now: Date = new Date()): SubjectCountdown {
  const start = daysUntil(w.window.startDate, now);
  const end = daysUntil(w.window.endDate, now);
  const isActive = start <= 0 && end >= 0;
  return { days: isActive ? 0 : start, date: new Date(w.window.startDate), isActive, windowName: w.window.name };
}

/** The next PEP sitting that covers this subject (by subject key or display name), or null. */
export function countdownForSubject(windows: PepWindowStatus[] | undefined, subject: string, now: Date = new Date()): SubjectCountdown | null {
  if (!windows?.length) return null;
  const m = SUBJECT_MATCHERS.find(x => x.test.test(subject));
  if (!m) return null;
  const candidates = windows
    .filter(w => m.window.test(w.window.name))
    .map(w => countdownForWindow(w, now))
    .filter(c => c.isActive || c.days > 0)
    .sort((a, b) => a.days - b.days);
  return candidates[0] ?? null;
}

/** Next sitting of any kind for the grade, or null. */
export function nextCountdown(windows: PepWindowStatus[] | undefined, now: Date = new Date()): SubjectCountdown | null {
  if (!windows?.length) return null;
  const list = windows.map(w => countdownForWindow(w, now)).filter(c => c.isActive || c.days > 0).sort((a, b) => a.days - b.days);
  return list.find(c => c.isActive) ?? list[0] ?? null;
}

export function formatExamDate(d: Date): string {
  return d.toLocaleDateString("en-JM", { timeZone: JAMAICA, weekday: "short", day: "numeric", month: "short" });
}

export function shortWindowName(name: string): string {
  const head = name.split(":")[0].trim();
  if (/language arts/i.test(head)) return "Language Arts";
  if (/math/i.test(head)) return "Maths";
  if (/ability/i.test(head)) return "Ability Test";
  if (/science/i.test(head) && /social/i.test(head)) return "Science and Social Studies";
  if (/science/i.test(head)) return "Science";
  if (/social/i.test(head)) return "Social Studies";
  return head.length > 24 ? head.slice(0, 22) + "…" : head;
}
